import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toastService from '../../services/toastService';
import { useResetPasswordMutation } from '../../app/services/authApi';
import { checkPasswordStrength } from '../../utils/password'; // Import the utility

import { MdLockReset } from 'react-icons/md';
import { Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [passwordStrength, setPasswordStrength] = useState(null); // New state for strength

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    if (password.length > 0) {
      setPasswordStrength(checkPasswordStrength(password));
    } else {
      setPasswordStrength(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toastService.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toastService.error('Passwords do not match');
      return;
    }

    const strength = checkPasswordStrength(newPassword);
    if (!strength.isStrong) {
      let errorMessage = 'Password is not strong enough. It must contain:';
      if (!strength.minLen) errorMessage += '\n- At least 8 characters';
      if (!strength.hasUpperCase) errorMessage += '\n- At least one uppercase letter';
      if (!strength.hasLowerCase) errorMessage += '\n- At least one lowercase letter';
      if (!strength.hasNumber) errorMessage += '\n- At least one number';
      if (!strength.hasSpecialChar) errorMessage += '\n- At least one special character';
      toastService.error(errorMessage);
      return;
    }

    try {
      await resetPassword({ token, newPassword, confirmPassword }).unwrap();
      toastService.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      toastService.error(error.data?.message || 'Failed to reset password, try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center secondary-background relative">
      <div className="max-w-md w-full tertiary-background backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-white/40 space-y-6">
        <h2 className="text-xl font-bold text-center text-primary">Reset Password</h2>
        <div className="h-20 w-20 mx-auto mb-6 secondary-background rounded-full flex items-center justify-center">
          <MdLockReset className="text-5xl text-primary" />
        </div>
        <p className="text-sm text-secondary text-center">
          Enter your new password below to reset your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={newPassword}
              onChange={handleNewPasswordChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 border-color shadow-sm"
            />
            <span
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-primary"
            >
              {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>
          {passwordStrength && (
            <div className="text-sm mt-2 space-y-1">
              <p className={passwordStrength.minLen ? 'text-green-500' : 'text-red-500'}>
                {passwordStrength.minLen ? '✓' : '✗'} 8 characters minimum
              </p>
              <p className={passwordStrength.hasUpperCase ? 'text-green-500' : 'text-red-500'}>
                {passwordStrength.hasUpperCase ? '✓' : '✗'} At least one uppercase letter
              </p>
              <p className={passwordStrength.hasLowerCase ? 'text-green-500' : 'text-red-500'}>
                {passwordStrength.hasLowerCase ? '✓' : '✗'} At least one lowercase letter
              </p>
              <p className={passwordStrength.hasNumber ? 'text-green-500' : 'text-red-500'}>
                {passwordStrength.hasNumber ? '✓' : '✗'} At least one number
              </p>
              <p className={passwordStrength.hasSpecialChar ? 'text-green-500' : 'text-red-500'}>
                {passwordStrength.hasSpecialChar ? '✓' : '✗'} At least one special character
              </p>
            </div>
          )}
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 border-color shadow-sm"
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-primary"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-4 py-2 rounded-lg text-sm font-medium white-text primary-background hover:opacity-90 transition-all duration-200 shadow-md"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div>
          <p
            onClick={() => navigate('/login')}
            className="text-primary hover:underline cursor-pointer flex justify-center mt-3"
          >
            Back to Login
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
